'use client';

import { useState } from 'react';

import { Button } from '@depop/web-thrift-ui/Button';

import styles from './styles.module.css';

import { FIGMA_CAPTURE_SCRIPT_URL } from '@/modules/adminTool/constants';

function loadScript(): Promise<void> {
  if (window.figma) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = FIGMA_CAPTURE_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load the Figma capture script'));
    document.head.appendChild(script);
  });
}

type CaptureStatus = 'idle' | 'loading' | 'launched' | 'error';

export function FigmaCapture() {
  const [status, setStatus] = useState<CaptureStatus>('idle');

  function handleError(error: unknown) {
    // The error hint tells admins to check the console, so make sure something
    // lands there whenever loading or opening the capture fails.
    console.error('[FigmaCapture] capture failed', error);
    setStatus('error');
  }

  async function launchToolbar() {
    setStatus('loading');
    try {
      await loadScript();

      if (typeof window.figma?.captureForDesign !== 'function') {
        throw new Error('Figma capture script is not available');
      }

      // captureForDesign renders html.to.design's own capture toolbar at the top
      // of the page and performs the copy from there. Its promise never resolves
      // back to us, so we launch it fire-and-forget and point the admin at that
      // toolbar to finish the capture.
      window.figma.captureForDesign({ selector: 'body' }).catch(handleError);
      setStatus('launched');
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        Open the Figma capture toolbar, then use it to copy this page into any
        Figma file.
      </p>
      <Button fullWidth disabled={status === 'loading'} onClick={launchToolbar}>
        Open Figma capture toolbar
      </Button>
      {status === 'launched' && (
        <p className={styles.hint}>
          Capture toolbar opened at the top of the page. Use it to capture the
          page, then paste (⌘V) into any Figma file.
        </p>
      )}
      {status === 'error' && (
        <p className={styles.errorHint}>
          Figma capture failed. Check the console for details.
        </p>
      )}
    </div>
  );
}
